import React, { useState, useEffect } from 'react';
import { NextUIProvider } from '@nextui-org/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import MainLayout from './layouts/MainLayout';
import HomePage from './pages/HomePage';
import TournamentPage from './pages/TournamentPage';
import TournamentDetailPage from './pages/TournamentDetailPage';
import WorldCupOfDartsPage from './pages/WorldCupOfDartsPage';
import ProTournamentInfoPage from './pages/ProTournamentInfoPage';
import ClubsPage from './pages/ClubsPage';
import ClubDetailPage from './pages/ClubDetailPage';
import PubsPage from './pages/PubsPage';
import PubDetailPage from './pages/PubDetailPage';
import ShopsPage from './pages/ShopsPage';
import ShopDetailPage from './pages/ShopDetailPage';
import SeriesPage from './pages/SeriesPage';
import SeriesDetailPage from './pages/SeriesDetailPage';
import RankingsPage from './pages/RankingsPage';
import ForumPage from './pages/ForumPage';
import ForumTopicPage from './pages/ForumTopicPage';
import SpielePage from './pages/SpielePage';
import CommunityPage from './pages/CommunityPage';
import NewsDetailPage from './pages/NewsDetailPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import FavoritesPage from './pages/FavoritesPage';
import ManagementPage from './pages/ManagementPage';
import EditClubPage from './pages/EditClubPage';
import CreateClubPage from './pages/CreateClubPage';
import EditPubPage from './pages/EditPubPage';
import CreatePubPage from './pages/CreatePubPage';
import EditShopPage from './pages/EditShopPage';
import CreateShopPage from './pages/CreateShopPage';
import EditSeriesPage from './pages/EditSeriesPage';
import CreateSeriesPage from './pages/CreateSeriesPage';
import EditTournamentPage from './pages/EditTournamentPage';
import CreateTournamentPage from './pages/CreateTournamentPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ProfilePage from './pages/ProfilePage';
import TournamentRegistrationPage from './pages/TournamentRegistrationPage';
import MyRegistrationsPage from './pages/MyRegistrationsPage';
import TeamInvitationPage from './pages/TeamInvitationPage';
import ChatsPage from './pages/ChatsPage';
import SetupDetailPage from './pages/SetupDetailPage';
import NearbyPage from './pages/NearbyPage';
import StatsExplorerPage from './pages/StatsExplorerPage';
import PlayerExplorerPage from './pages/PlayerExplorerPage';
import SeasonStatsPage from './pages/SeasonStatsPage';
import PredictionGamesPage from './pages/PredictionGamesPage';
import PredictionGameDetailPage from './pages/PredictionGameDetailPage';
import SearchPage from './pages/SearchPage';
import MatchDetailPage from './pages/MatchDetailPage';
import LobbiesPage from './pages/LobbiesPage';
import LobbyDetailPage from './pages/LobbyDetailPage';
import EventsTimelinePage from './pages/EventsTimelinePage';
import ContactPage from './pages/ContactPage';
import DynamicPage from './pages/DynamicPage';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import AdminRoute from './components/admin/AdminRoute';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminNewsAutomationPage from './pages/admin/AdminNewsAutomationPage';
import AdminProTournamentsPage from './pages/admin/AdminProTournamentsPage';
import AdminPredictionGamesPage from './pages/admin/AdminPredictionGamesPage';
import AdminPredictionGameDetailPage from './pages/admin/AdminPredictionGameDetailPage';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import healthService from './services/healthService';
import { AuthProvider } from './contexts/AuthContext';
import { SearchExplorerProvider } from './contexts/SearchExplorerContext';
import { ThemeProvider } from './contexts/ThemeContext';

function App() {
  const [isBackendHealthy, setIsBackendHealthy] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Health Service starten
    healthService.startHealthCheck(10000); // 10 Sekunden Intervall

    // Health Status Listener
    const unsubscribe = healthService.onHealthChange((isHealthy) => {
      setIsBackendHealthy(isHealthy);
      if (!isInitialized) {
        setIsInitialized(true);
      }
    });

    // Cleanup bei Unmount
    return () => {
      unsubscribe();
      healthService.cleanup();
    };
  }, [isInitialized]);

  const handleRetry = () => {
    // Retry wird vom Health Service automatisch gehandelt
    console.log('Manual retry triggered');
  };

  return (
    <HelmetProvider>
     <ThemeProvider>
      <NextUIProvider>
        <Router>
          <ScrollToTop />
          <AuthProvider>
           <SearchExplorerProvider>
            {/* Theme (dark/light) kommt über die Klasse auf <html> – siehe ThemeContext */}
            <div className="bg-black text-white min-h-screen">
              <Routes>
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="spiele" element={<SpielePage />} />
                  <Route path="spiel/:eventId" element={<MatchDetailPage />} />
                  <Route path="turniere" element={<TournamentPage />} />
                  <Route path="turniere/pdc-world-cup-of-darts" element={<WorldCupOfDartsPage />} />
                  <Route path="turniere/:slug" element={<ProTournamentInfoPage />} />
                  <Route path="turnier/:slug" element={<TournamentDetailPage />} />
                  <Route path="turnier/:slug/anmelden" element={<TournamentRegistrationPage />} />
                  <Route path="team-einladung/:code" element={<TeamInvitationPage />} />
                  {/* Alias: Backend-E-Mails verlinken auf /team/join/:code */}
                  <Route path="team/join/:code" element={<TeamInvitationPage />} />
                  <Route path="tippspiele" element={<PredictionGamesPage />} />
                  <Route path="tippspiele/:id" element={<PredictionGameDetailPage />} />
                  <Route path="setup/:setupId" element={<SetupDetailPage />} />
                  <Route path="suchen" element={<SearchPage />} />
                  <Route path="kontakt" element={<ContactPage />} />
                  <Route path="dartcloud/:slug" element={<DynamicPage />} />
                  <Route path="in-der-naehe" element={<NearbyPage />} />
                  <Route path="statistiken" element={<StatsExplorerPage />} />
                  <Route path="statistiken/spieler/:playerId" element={<PlayerExplorerPage />} />
                  <Route path="statistiken/saison/:seasonStageId" element={<SeasonStatsPage />} />
                  <Route path="vereine" element={<ClubsPage />} />
                  <Route path="verein/:slug" element={<ClubDetailPage />} />
                  <Route path="spielstaetten" element={<PubsPage />} />
                  <Route path="spielstaette/:slug" element={<PubDetailPage />} />
                  <Route path="shops" element={<ShopsPage />} />
                  <Route path="shop/:slug" element={<ShopDetailPage />} />
                  <Route path="turnierserien" element={<SeriesPage />} />
                  <Route path="turnierserie/:slug" element={<SeriesDetailPage />} />
                  <Route path="ranglisten" element={<RankingsPage />} />
                  <Route path="ranglisten/:slug" element={<RankingsPage />} />
                  <Route path="kalender" element={<EventsTimelinePage />} />
                  <Route path="forum" element={<ForumPage />} />
                  <Route path="forum/topic/:slug" element={<ForumTopicPage />} />
                  <Route path="forum/:categorySlug" element={<ForumPage />} />
                  <Route
                    path="admin"
                    element={
                      <AdminRoute>
                        <AdminDashboardPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/news-automation"
                    element={
                      <AdminRoute>
                        <AdminNewsAutomationPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/pro-turniere"
                    element={
                      <AdminRoute>
                        <AdminProTournamentsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/tippspiele"
                    element={
                      <AdminRoute>
                        <AdminPredictionGamesPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/tippspiele/:id"
                    element={
                      <AdminRoute>
                        <AdminPredictionGameDetailPage />
                      </AdminRoute>
                    }
                  />
                  <Route path="community" element={<CommunityPage />} />
                  <Route path="community/lobbys" element={<LobbiesPage />} />
                  <Route path="community/lobby/:lobbyId" element={<LobbyDetailPage />} />
                  <Route path="news/:slug" element={<NewsDetailPage />} />
                  <Route path="spieler/:slug" element={<PlayerProfilePage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="passwort-vergessen" element={<ForgotPasswordPage />} />
                  <Route
                    path="profil"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="favoriten"
                    element={
                      <ProtectedRoute>
                        <FavoritesPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Alt-/Alias-URLs "meine/*" → bestehende Seiten (Profil "Meine Inhalte") */}
                  <Route path="meine/turniere" element={<Navigate to="/verwaltung?tab=tournaments" replace />} />
                  <Route path="meine/serien" element={<Navigate to="/verwaltung?tab=series" replace />} />
                  <Route path="meine/vereine" element={<Navigate to="/verwaltung?tab=clubs" replace />} />
                  <Route path="meine/spielstaetten" element={<Navigate to="/verwaltung?tab=pubs" replace />} />
                  <Route path="meine/shops" element={<Navigate to="/verwaltung?tab=shops" replace />} />
                  <Route path="meine/favoriten" element={<Navigate to="/favoriten" replace />} />
                  <Route
                    path="meine-anmeldungen"
                    element={
                      <ProtectedRoute>
                        <MyRegistrationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="nachrichten"
                    element={
                      <ProtectedRoute>
                        <ChatsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="nachrichten/:chatId"
                    element={
                      <ProtectedRoute>
                        <ChatsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung"
                    element={
                      <ProtectedRoute>
                        <ManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/verein/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateClubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/verein/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditClubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/spielstaette/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreatePubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/spielstaette/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditPubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/shop/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateShopPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/shop/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditShopPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnierserie/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateSeriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnierserie/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditSeriesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/erstellen"
                    element={
                      <ProtectedRoute>
                        <CreateTournamentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="verwaltung/turnier/:id/bearbeiten"
                    element={
                      <ProtectedRoute>
                        <EditTournamentPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Routes>

              {/* Maintenance Overlay */}
              <MaintenanceOverlay
                isOpen={isInitialized && !isBackendHealthy}
                onRetry={handleRetry}
              />
            </div>
           </SearchExplorerProvider>
          </AuthProvider>
        </Router>
      </NextUIProvider>
     </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;